import { useEffect, useRef } from "react";
import { useI18n } from "../i18n/I18nProvider";
import { IconInstagram, IconLinkedin, IconWhatsapp } from "./icons";

const CONTACT_LINKS = [
  { key: "whatsapp", href: "https://wa.me/966559557835", icon: IconWhatsapp },
  { key: "instagram", href: "https://www.instagram.com/adbond.sa/", icon: IconInstagram },
  { key: "linkedin", href: "https://www.linkedin.com/company/bond-sa/", icon: IconLinkedin },
] as const;

/**
 * The failure state has no design frame, so it follows the success proportions. The retry is
 * offered exactly once: after the first failed retry the button disappears and the page keeps the
 * direct contact channels and the way back to the form.
 */
export function SubmitErrorView({
  retryUsed,
  onRetry,
  onEdit,
}: {
  retryUsed: boolean;
  onRetry: () => void;
  onEdit: () => void;
}) {
  const { copy } = useI18n();
  const t = copy.submitState;
  const heading = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    heading.current?.focus();
  }, [retryUsed]);

  return (
    <div className="flex flex-1 flex-col items-center justify-center px-6 py-12 text-center">
      <span className="relative flex h-40 w-40 items-center justify-center rounded-full bg-danger-bg lg:h-[196px] lg:w-[196px]">
        <span className="flex h-[124px] w-[124px] items-center justify-center rounded-full bg-danger lg:h-[154px] lg:w-[154px]">
          <svg viewBox="0 0 124 124" aria-hidden="true" className="h-16 w-16 lg:h-20 lg:w-20">
            <path d="M62 32v38" stroke="#ffffff" strokeWidth="11" strokeLinecap="round" />
            <circle cx="62" cy="90" r="7" fill="#ffffff" />
          </svg>
        </span>
      </span>

      <div
        role="alert"
        className="mt-6 flex max-w-[700px] flex-col items-center gap-2.5 lg:mt-7 lg:gap-3"
      >
        <h1
          ref={heading}
          tabIndex={-1}
          className="text-[25px] font-bold leading-tight text-navy outline-none lg:text-[40px]"
        >
          {retryUsed ? t.finalHeading : t.errorHeading}
        </h1>
        <p className="text-[13px] leading-relaxed text-body lg:text-[17px]">
          {retryUsed ? t.finalBody : t.errorBody}
        </p>
      </div>

      {retryUsed ? null : (
        <button
          type="button"
          onClick={onRetry}
          className="gradient-cta mt-8 inline-flex h-[54px] w-[302px] max-w-full items-center justify-center rounded-xl text-[15px] font-bold text-white shadow-card lg:h-[58px] lg:w-[300px]"
        >
          {t.retry}
        </button>
      )}

      <p className="mt-8 text-[13px] font-semibold text-navy lg:mt-9 lg:text-[14px]">
        {t.contactTitle}
      </p>
      <ul
        aria-label={t.contactTitle}
        className="mt-4 flex max-w-[680px] flex-wrap items-center justify-center gap-2"
      >
        {CONTACT_LINKS.map(({ key, href, icon: Icon }) => (
          <li key={key}>
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-10 items-center gap-2 rounded-full border border-line bg-white px-4 text-[12px] font-semibold text-navy transition-colors hover:border-tint-line hover:bg-tint-2"
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span>{copy.social[key]}</span>
            </a>
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={onEdit}
        className="mt-6 text-[13px] font-semibold text-purple underline-offset-4 hover:underline"
      >
        {t.edit}
      </button>
    </div>
  );
}
